"use client";

import React from "react";
import { ArrowRight, Play, Activity, ShieldCheck, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
export default function AdvancedKidneyCareForABetterLife() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const carouselImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_junk_removal_team_couch.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_junk_removal_team_couch.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_junk_removal_team_couch.png-wvc-sizes",
    alt: "Professional JUNKR team in black polos carrying a heavy sofa from a Kanata home"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junkr_trailer_loading_ottawa.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junkr_trailer_loading_ottawa.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junkr_trailer_loading_ottawa.png-wvc-sizes",
    alt: "Clean modern JUNKR trailer being loaded with furniture in a Nepean driveway"
  }];
  return <section data-section-id="238487" id="junk-removal-made-simple" className="relative overflow-hidden bg-background pt-24 pb-16 lg:pt-32 lg:pb-24">
      {}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -bottom-[10%] -left-[5%] w-[50%] h-[60%] rounded-lg bg-primary/20 blur-[120px] opacity-40" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[50%] rounded-lg bg-muted/10 blur-[120px] opacity-30" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium mb-6 w-fit">
              <Activity className="size-4" />
              <span>Ottawa's Local Junk Experts</span>
            </div>
            
            <h1 className="font-default text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.1]">
              Junk Removal Made Simple —{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                Same-Day Service Available Across Ottawa
              </span>
            </h1>
            
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground/60 max-w-[90%] leading-relaxed">
              Affordable same-day junk removal with friendly local service. No hidden fees, free estimates.
            </p>

            <p className="mt-3 text-sm font-medium text-secondary flex items-center gap-2">
              <span className="size-1.5 rounded-lg bg-secondary animate-pulse" />
              Same-day service available • No obligation
            </p>
            
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sticky bottom-4 sm:relative sm:bottom-0 z-50 bg-background/80 sm:bg-transparent backdrop-blur-md sm:backdrop-blur-none p-4 sm:p-0 rounded-2xl shadow-2xl sm:shadow-none border border-border sm:border-none">
              <Button size="lg" asChild className="h-14 sm:h-12 px-8 text-base bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg font-bold">
                <Link to="/PricingPage">
                  Get a Free Quote
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button size="lg" asChild className="h-14 sm:h-12 px-8 text-base bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg font-bold">
                <Link to="#">
                  <Play className="mr-2 size-5" />
                  Text Us Photos
                </Link>
              </Button>
            </div>

            <div className="mt-10 flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-5 text-primary" />
                  <span>Locally Owned</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-5 text-primary" />
                  <span>Fully Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-5 text-primary" />
                  <span>Same-Day Service</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <CircleCheck className="size-5" />
                <span>No Hidden Fees</span>
              </div>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative w-full aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-secondary/20 rounded-3xl transform rotate-3 scale-105 -z-10 transition-transform duration-700 hover:rotate-6" />
              
              {}
              <div className="w-full h-full bg-muted relative overflow-hidden shadow-2xl rounded-2xl">
                <Carousel className="w-full h-full" opts={{
                loop: true
              }}>
                  <CarouselContent className="h-full ml-0">
                    {carouselImages.map((image, index) => <CarouselItem key={index} className="pl-0 h-full" data-index={index}>
                        <div className="relative w-full h-full">
                          <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="w-full h-full object-cover object-center" loading="lazy" decoding="async" />
                          <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 to-transparent pointer-events-none" />
                        </div>
                      </CarouselItem>)}
                  </CarouselContent>
                </Carousel>
              </div>

              {}
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-2xl shadow-xl border border-border flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500 z-20">
                <div className="flex -space-x-3">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/team_member_1.png" alt="Ottawa Team Member" className="size-10 rounded-lg border-2 border-card object-cover" />
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/team_member_2.png" alt="Ottawa Team Member" className="size-10 rounded-lg border-2 border-card object-cover" />
                  <div className="size-10 rounded-lg border-2 border-card bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                    +8
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground font-default">Local Crew</p>
                  <p className="text-xs text-muted-foreground">Ottawa Specialists</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}